'use memo'

import React, { useEffect, useState } from 'react'
import { useModalScroll } from 'src/hooks/useModalScroll'
import { EditPlayerProvider } from './EditPlayerContext'
import EditPlayerModalContent from './EditPlayerModalContent'

const TRANSITION_DURATION = 300

interface Props {
  isOpen: boolean
  onClose: () => void
  /**
   * When provided, the player is fetched (GET /players/:id via React Query) and
   * the form is pre-populated. When absent, the form opens empty and no fetch
   * happens. Any feature can reuse this modal as an entry point by passing a
   * playerId.
   */
  playerId?: string
  onSuccess?: () => void
}

const EditPlayerModal: React.FC<Props> = ({
  isOpen,
  onClose,
  playerId,
  onSuccess,
}) => {
  const { openModal, closeModal } = useModalScroll()
  const [isMounted, setIsMounted] = useState(false)
  const [isVisible, setIsVisible] = useState(false)

  useEffect(() => {
    if (isOpen) {
      openModal()
      setIsMounted(true)
      requestAnimationFrame(() => {
        requestAnimationFrame(() => setIsVisible(true))
      })
    } else {
      setIsVisible(false)
      const timer = setTimeout(() => {
        setIsMounted(false)
        closeModal()
      }, TRANSITION_DURATION)
      return () => clearTimeout(timer)
    }
  }, [isOpen, openModal, closeModal])

  if (!isMounted) {
    return null
  }

  return (
    <EditPlayerProvider
      onClose={onClose}
      onSuccess={onSuccess}
      playerId={playerId}
    >
      <EditPlayerModalContent
        isVisible={isVisible}
        transitionDuration={TRANSITION_DURATION}
      />
    </EditPlayerProvider>
  )
}

export default EditPlayerModal
